import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useComponentMount } from 'utilities/hooks.util';

import {
  resetAutodesk,
  addSameAutodeskFileLoaded,
  getAutodeskFileStart,
  getAutodeskAccessTokenStart
} from 'redux/autodesk/actions';
import {
  selectAutodeskLoadingTokenError,
  selectAutodeskLoadingFileError,
  selectAutodeskAuthInfo,
  selectAutodeskFile
} from 'redux/autodesk/selectors';
import { submitCollect } from 'redux/track/actions';
import { EntityType } from 'types';

const AutodeskViewer: React.FC<Props> = ({
  fileId,
  enquiryId,
  onDocumentError,
  onDocumentLoaded
}) => {
  const [scriptLoaded, setScriptLoaded] = useState<boolean>(false);
  const [isViewerReady, setIsViewerReady] = useState<boolean>(false);
  const dispatch: any = useDispatch();
  const authInfo = useSelector(selectAutodeskAuthInfo);
  const file = useSelector(selectAutodeskFile);
  const loadingTokenError = useSelector(selectAutodeskLoadingTokenError);
  const loadingFileError = useSelector(selectAutodeskLoadingFileError);
  const viewerDivRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<any>(null);
  const documentLoaded = useRef<boolean>(false);

  const getAccessToken = useCallback(
    (callback: Function) => {
      if (authInfo?.accessToken && authInfo?.expiresIn) {
        callback(authInfo.accessToken, authInfo.expiresIn);
      }
    },
    [authInfo]
  );

  const viewerReady = () => {
    const { Autodesk } = window;
    if (Autodesk) {
      const config = {
        extensions: ['Autodesk.DocumentBrowser']
      };
      const viewer = new Autodesk.Viewing.GuiViewer3D(
        viewerDivRef.current,
        config
      );
      const startedCode = viewer.start();
      viewer.setTheme('light-theme');
      viewerRef.current = viewer;
      if (startedCode === 0) {
        setIsViewerReady(true);
      }
      return viewer;
    }
  };

  const initViewer = useCallback(() => {
    const { Autodesk } = window;
    if (Autodesk) {
      Autodesk.Viewing.Initializer(
        { env: 'AutodeskProduction', getAccessToken },
        viewerReady
      );
    }
  }, [getAccessToken]);

  useEffect(() => {
    if (
      isViewerReady &&
      file?.urn &&
      viewerRef.current &&
      !documentLoaded.current
    ) {
      const { Autodesk } = window;
      const onDocumentLoadSuccess = (doc: any) => {
        viewerRef.current.loadDocumentNode(
          doc,
          doc.getRoot().getDefaultGeometry()
        );
        if (typeof onDocumentLoaded === 'function') {
          onDocumentLoaded();
        }
      };
      const onDocumentLoadFailure = (code: any, message: any, errors: any) => {
        console.error(code, message, errors);
      };
      viewerRef.current.setLightPreset(0);
      Autodesk.Viewing.Document.load(
        `urn:${file.urn}`,
        onDocumentLoadSuccess,
        onDocumentLoadFailure
      );
      documentLoaded.current = true;
    }
  }, [file, isViewerReady, onDocumentLoaded]);

  useEffect(() => {
    if (scriptLoaded) {
      const { Autodesk } = window;
      if (Autodesk && authInfo?.accessToken && authInfo.expiresIn) {
        initViewer();
      }
    }
  }, [scriptLoaded, authInfo, initViewer]);

  useEffect(() => {
    if (loadingFileError || loadingTokenError) {
      if (typeof onDocumentError === 'function') {
        onDocumentError();
      }
    }
  }, [loadingFileError, loadingTokenError, onDocumentError]);

  useComponentMount(() => {
    const { Autodesk } = window;

    if (file?.id === fileId) {
      dispatch(addSameAutodeskFileLoaded());
      setScriptLoaded(true);
    } else {
      if (!Autodesk) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.addEventListener('load', function () {
          setScriptLoaded(true);
        });
        script.src =
          'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/viewer3D.js';
        document.body.appendChild(script);
      } else {
        setScriptLoaded(true);
      }
      dispatch(getAutodeskFileStart(fileId, enquiryId));
      dispatch(getAutodeskAccessTokenStart(enquiryId));
    }

    dispatch(
      submitCollect({
        payload: { fileId, enquiryId },
        type: EntityType.VIEW_DWF_FILE
      })
    );

    return () => {
      dispatch(resetAutodesk());
    };
  });

  return (
    <div className="autodesk-viewer-container">
      <link
        rel="stylesheet"
        type="text/css"
        href="https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/style.css"
      />
      <div className="autodesk-viewer" ref={viewerDivRef}></div>
      <div className="autodesk-viewer-overlay"></div>
    </div>
  );
};

type Props = {
  fileId: string;
  enquiryId?: number;
  onDocumentError?: Function;
  onDocumentLoaded?: Function;
};

export default AutodeskViewer;
